import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Container,
  Fill,
  LevelIcon,
  LevelProgressBar,
  LevelProgressContainer,
  LevelTitle,
  LevelUpAnimation,
  PointsChangeIndicator,
  PointsLabel,
} from './styles';
import { ILevelDisplaysProps } from '../../dtos/IILevelDisplaysProps';
import { useAuth } from '../../hooks/auth';
import { ILevelProps } from '../../dtos/ILevelProps';

// import { Container } from './styles';

const LevelDisplayComponent: React.FC<ILevelDisplaysProps> = ({
  displayMode,
  current_points,
}) => {
  const { user } = useAuth();
  const [progress, setProgress] = React.useState(0);
  const targetProgress = useRef(0);
  const animationStarted = useRef(false);
  const [showPointsAnimation, setShowPointsAnimation] = useState(false);
  const [showLevelUpAnimation, setShowLevelUpAnimation] = useState(false);
  const [effectivePoints, setEffectivePoints] = useState(
    user.total_points ?? 0,
  );
  const [currentLevel, setCurrenLevel] = useState<ILevelProps | undefined>(
    user.current_level,
  );
  const [pointsDelta, setPointsDelta] = useState(0);
  const previousPoints = useRef(user.total_points ?? 0);
  const previousLevel = useRef(user.current_level?.name ?? '');

  const effectiveName = currentLevel?.name;
  const handleProgress = useCallback((): number => {
    if (currentLevel && currentLevel.max_points > 0) {
      return Math.min((effectivePoints / currentLevel.max_points) * 100, 100);
    }
    return 0;
  }, [effectivePoints, currentLevel]);

  useEffect(() => {
    if (user.total_points !== undefined) {
      setEffectivePoints(user.total_points);
    }
    if (user.current_level) {
      setCurrenLevel(user.current_level);
    }
  }, [user]);

  useEffect(() => {
    targetProgress.current = handleProgress();

    // Start with empty bar
    if (!animationStarted.current) {
      setProgress(0);
      previousPoints.current = effectivePoints;
      previousLevel.current = effectiveName || '';

      const timer = setTimeout(() => {
        setProgress(targetProgress.current);
        animationStarted.current = true;
      }, 100);

      // Return the cleanup function instead of calling it immediately
      return () => clearTimeout(timer);
    }

    // After initial animation, update directly
    setProgress(targetProgress.current);
    return undefined;
  }, [handleProgress, effectivePoints, effectiveName]);

  // Points change animation
  useEffect(() => {
    if (
      animationStarted.current &&
      previousPoints.current !== effectivePoints
    ) {
      // Calculate the change in points
      const delta = effectivePoints - previousPoints.current;
      setPointsDelta(delta);

      // Show points change animation
      setShowPointsAnimation(true);
      const timer = setTimeout(() => {
        setShowPointsAnimation(false);
      }, 2000);

      // Update previous points
      previousPoints.current = effectivePoints;

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [effectivePoints]);

  // Level up animation
  useEffect(() => {
    if (
      animationStarted.current &&
      previousLevel.current !== '' &&
      previousLevel.current !== effectiveName
    ) {
      // Show level up celebration animation
      setShowLevelUpAnimation(true);
      const timer = setTimeout(() => {
        setShowLevelUpAnimation(false);
      }, 3000);

      // Update previous level
      previousLevel.current = effectiveName || '';

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [effectiveName]);

  return (
    <Container>
      <LevelIcon src={currentLevel?.icon_url} alt="level" />
      <LevelProgressContainer $displayMode={displayMode}>
        <LevelTitle> Nv. {effectiveName} </LevelTitle>
        <LevelProgressBar>
          <Fill $progress={progress} />
        </LevelProgressBar>
        <PointsLabel>
          {effectivePoints} pontos
          {showPointsAnimation && (
            <PointsChangeIndicator $isPositive={pointsDelta > 0}>
              {pointsDelta > 0 ? '+' : ''}
              {pointsDelta}
            </PointsChangeIndicator>
          )}
        </PointsLabel>
      </LevelProgressContainer>
      {showLevelUpAnimation && (
        <LevelUpAnimation>Subiu de nível!</LevelUpAnimation>
      )}
    </Container>
  );
};

export default LevelDisplayComponent;
